import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import Marquee from 'react-fast-marquee';
import {
  ArrowRight, Zap, Eye, Film, Users, LayoutGrid, Send,
  Sparkles, Wand2, Camera, Aperture, Move, Sun,
  Image as ImageIcon, Video, Music, Palette, Scissors,
  Star, Flame, Snowflake, Shirt, Layers, Paintbrush,
  Brain, Mic, Quote, ChevronRight, Clock, CheckCircle2,
  PlayCircle, Pause, Heart, Loader2
} from 'lucide-react';

// ── Assets ────────────────────────────────────────────────────
const IMG_HERO_BG = 'https://static.prod-images.emergentagent.com/jobs/e9cdd80e-0b21-42db-a484-279ac9d6e17d/images/ce286cbbf7e596c6000c2bc2cc4cddd215941879dca515466fac756d300ecd96.png';
const IMG_CINEMA_LENS = 'https://static.prod-images.emergentagent.com/jobs/e9cdd80e-0b21-42db-a484-279ac9d6e17d/images/6af0a20d18a9d49ad64b19243ec9ce4de5d093aaf475e6d560f808cccf881245.png';
const IMG_ORACLE_EYE = 'https://static.prod-images.emergentagent.com/jobs/e9cdd80e-0b21-42db-a484-279ac9d6e17d/images/5909104216d908efc4742ef2e32736e49ea87290bdd3b5e8d8c3c025a0d86a8b.png';
const IMG_MARBLE = 'https://images.pexels.com/photos/2117938/pexels-photo-2117938.jpeg';

// Generated influencer assets (Nano Banana via Delphi backend)
const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
const IMG_CHAR_MALE = `${API_BASE}/api/media/images/56b92109-f7c6-44d4-8a62-92ad1e764f94.png`;
const IMG_CHAR_FEMALE = 'https://customer-assets.emergentagent.com/job_e9cdd80e-0b21-42db-a484-279ac9d6e17d/artifacts/202nonpk_47e6c889.png';
// Sora 2 short cinematic clips (autoplay) — populated after generation
const VIDEO_CHAR_MALE = `${API_BASE}/api/media/videos/cf022416-38e3-40f7-9b88-81c202e2f3cc.mp4`;
const VIDEO_CHAR_FEMALE = `${API_BASE}/api/media/videos/0d8063d3-d9ae-41a7-908c-26e0efa617e9.mp4`;

// Prophecy reel thumbnails (Nano Banana via Delphi backend)
const PROPHECY_IMGS = {
  sneaker: `${API_BASE}/api/media/images/d7e2f8af-afc1-417e-bbd1-2dfe95c3a34a.png`,
  watch: `${API_BASE}/api/media/images/856f4ca2-65ac-4a82-81b5-4681f1190d94.png`,
  cyber: `${API_BASE}/api/media/images/c6614a90-6ea3-4233-a40f-6df3e1c135e8.png`,
  oat: `${API_BASE}/api/media/images/1d421f93-0527-431c-814c-6bad410ec6a8.png`,
  tokyo: `${API_BASE}/api/media/images/69285cbe-5802-491f-8831-ea1f52e6cd10.png`,
  cosmetic: `${API_BASE}/api/media/images/cdc97bed-71f2-48fd-9feb-062897333f58.png`,
  carousel: `${API_BASE}/api/media/images/47a6be34-ac4f-4f5c-b9ac-96a77ec4ea75.png`,
  greek: `${API_BASE}/api/media/images/43c8de3a-22fb-4b7e-8d50-c292a779fd57.png`,
};

// ── Data ──────────────────────────────────────────────────────
const PANTHEON = [
  { name: 'SORA 2', meta: 'Video' },
  { name: 'NANO BANANA', meta: 'Image' },
  { name: 'FLUX KONTEXT', meta: 'Image' },
  { name: 'GPT IMAGE 1', meta: 'Image' },
  { name: 'HEYGEN', meta: 'Avatar' },
  { name: 'ELEVENLABS', meta: 'Voice' },
  { name: 'SUNO AI', meta: 'Music' },
  { name: 'CLAUDE 4.5', meta: 'Reason' },
];

const PROPHECY_PRESETS = [
  "Render a cyberpunk product launch for a neon sneaker — moodboard, hero shot, and a 4-second cinematic teaser.",
  "Generate three product hero shots for a luxury watch on marble, then draft Instagram captions.",
  "Plan a 5-step launch campaign for an oat milk brand. Hero image, billboard mockup, and 3 social variants.",
  "Build a cyberpunk influencer with a cloned voice, then animate her saying my brand's tagline.",
];

const ORCHESTRATION_STEPS = [
  { icon: Brain, label: 'Composing the plan', tool: 'Claude 4.5', duration: 1600 },
  { icon: ImageIcon, label: 'Materialising images', tool: 'Flux + Nano Banana', duration: 2400 },
  { icon: Video, label: 'Summoning motion', tool: 'Sora 2', duration: 2200 },
  { icon: LayoutGrid, label: 'Arranging on Canvas', tool: 'Delphi Canvas', duration: 1200 },
];

const CINEMA_PRESETS = [
  { id: 'arri', label: 'ARRI Alexa · Anamorphic 40mm', filter: 'brightness(0.95) saturate(1.15) contrast(1.05)', tint: 'rgba(255,160,80,0.08)', shot: 'A lone astronaut walks across an alien desert at dusk. Two moons rising.' },
  { id: 'red', label: 'RED V-Raptor · Prime 85mm f/1.4', filter: 'brightness(1.05) saturate(1.25) contrast(1.15)', tint: 'rgba(50,150,255,0.06)', shot: 'Slow push toward a CEO at a glass desk, golden hour spilling through Tokyo skyline.' },
  { id: 'sony', label: 'Sony Venice 2 · Macro 100mm', filter: 'brightness(0.9) saturate(0.85) contrast(1.25)', tint: 'rgba(255,80,40,0.12)', shot: 'Macro on a single dewdrop sliding down a black orchid petal. Vertigo zoom out.' },
  { id: 'bm', label: 'Blackmagic URSA · Anamorphic 35mm', filter: 'brightness(0.85) saturate(0.7) contrast(1.35) hue-rotate(-8deg)', tint: 'rgba(140,80,255,0.10)', shot: 'A vintage motorcycle drifts through neon rain in a Hong Kong alley.' },
];

const LIVE_PROPHECIES = [
  { user: '@marina.x', mins: 2, what: 'Cinematic sneaker reveal', kind: 'video', img: PROPHECY_IMGS.sneaker },
  { user: '@jeromek', mins: 5, what: 'Luxury watch hero shot', kind: 'image', img: PROPHECY_IMGS.watch },
  { user: '@studio.nyx', mins: 8, what: 'Cyberpunk character + voice', kind: 'character', img: PROPHECY_IMGS.cyber },
  { user: '@oatful.co', mins: 12, what: 'Oat milk launch campaign', kind: 'campaign', img: PROPHECY_IMGS.oat },
  { user: '@taro.films', mins: 14, what: 'Tokyo night drone shot', kind: 'video', img: PROPHECY_IMGS.tokyo },
  { user: '@bloom.studio', mins: 19, what: 'Cosmetic packshot trio', kind: 'image', img: PROPHECY_IMGS.cosmetic },
  { user: '@dao.threads', mins: 23, what: 'IG carousel — 6 frames', kind: 'campaign', img: PROPHECY_IMGS.carousel },
  { user: '@marble.lab', mins: 27, what: 'Greek statue x cyberpunk fusion', kind: 'character', img: PROPHECY_IMGS.greek },
];

const COMPARISON = [
  { label: 'Brief & moodboard', old: '4 hrs', oracle: '12 sec' },
  { label: 'Hero image variations', old: '8 hrs', oracle: '45 sec' },
  { label: 'Cinematic teaser', old: '2 days', oracle: '90 sec' },
  { label: 'Voiceover + character', old: '1 week', oracle: '3 min' },
  { label: 'Multi-platform campaign', old: '2-4 weeks', oracle: '7 min' },
];

const APPS = [
  { name: 'Outfit Swap', icon: Shirt }, { name: 'Headshot', icon: Camera },
  { name: 'Relight', icon: Star }, { name: 'BG Remove', icon: Scissors },
  { name: 'Upscale', icon: Wand2 }, { name: 'Color Grade', icon: Paintbrush },
  { name: 'Style Snap', icon: Palette }, { name: 'Explosion VFX', icon: Flame },
  { name: 'Freeze Frame', icon: Snowflake }, { name: 'Glitch', icon: Zap },
  { name: 'Angles', icon: Aperture }, { name: 'Product Shot', icon: ImageIcon },
  { name: 'Billboard', icon: Film }, { name: 'Skin Enhance', icon: Sun },
  { name: 'Face Swap', icon: Users }, { name: 'Transitions', icon: Layers },
];

const USE_CASES = [
  { who: 'Creators', desc: 'Channel-ready posts, reels, and avatars without a studio.' },
  { who: 'Marketers', desc: 'URL → full ad campaign across 9 platforms in minutes.' },
  { who: 'Agencies', desc: 'Ship pitches and concepts before the meeting ends.' },
  { who: 'Founders', desc: 'Product hero shots, demos, and launches — all in one.' },
];

const fadeUp = { hidden: { opacity: 0, y: 24 }, visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } } };
const stagger = { visible: { transition: { staggerChildren: 0.08 } } };

// ── Decorative laurel divider ─────────────────────────────────
const LaurelDivider = ({ className = '' }) => (
  <div className={`flex items-center justify-center gap-3 ${className}`} aria-hidden>
    <svg width="40" height="10" viewBox="0 0 40 10" fill="none" className="text-orange-500/40">
      <path d="M0 5 Q 10 0, 20 5 T 40 5" stroke="currentColor" strokeWidth="0.5" fill="none" />
      <circle cx="20" cy="5" r="1" fill="currentColor" />
    </svg>
    <span className="text-orange-500/60 text-[10px] tracking-[0.4em]">⟡</span>
    <svg width="40" height="10" viewBox="0 0 40 10" fill="none" className="text-orange-500/40 scale-x-[-1]">
      <path d="M0 5 Q 10 0, 20 5 T 40 5" stroke="currentColor" strokeWidth="0.5" fill="none" />
      <circle cx="20" cy="5" r="1" fill="currentColor" />
    </svg>
  </div>
);

const SectionLabel = ({ numeral, label, className = '' }) => (
  <div className={`flex items-center gap-3 text-[10px] tracking-[0.35em] uppercase text-orange-400/70 font-semibold ${className}`}>
    <span className="font-serif text-[11px] italic text-orange-400/50" style={{ fontFamily: 'Georgia, serif' }}>{numeral}</span>
    <span className="h-px w-8 bg-gradient-to-r from-orange-500/60 to-transparent" />
    <span>{label}</span>
  </div>
);

// ── Interactive Prophecy Console ──────────────────────────────
const InteractiveProphecy = ({ onConsult }) => {
  const [draft, setDraft] = useState(PROPHECY_PRESETS[0]);
  const [phase, setPhase] = useState('idle'); // idle | planning | step0..3 | done
  const [activeStep, setActiveStep] = useState(-1);
  const [outputs, setOutputs] = useState([]); // [{type, ready}]

  const runProphecy = () => {
    if (!draft.trim() || phase !== 'idle' && phase !== 'done') return;
    setPhase('running');
    setActiveStep(0);
    setOutputs([]);
    let cumulative = 0;
    ORCHESTRATION_STEPS.forEach((step, i) => {
      cumulative += step.duration;
      setTimeout(() => {
        setActiveStep(i + 1);
        // reveal output tile
        const types = ['plan', 'image', 'video', 'canvas'];
        setOutputs(prev => [...prev, { type: types[i], ready: true }]);
        if (i === ORCHESTRATION_STEPS.length - 1) {
          setTimeout(() => setPhase('done'), 300);
        }
      }, cumulative);
    });
  };

  const reset = () => {
    setPhase('idle');
    setActiveStep(-1);
    setOutputs([]);
  };

  const cyclePreset = () => {
    const idx = PROPHECY_PRESETS.indexOf(draft);
    setDraft(PROPHECY_PRESETS[(idx + 1) % PROPHECY_PRESETS.length]);
  };

  const isRunning = phase === 'running';

  return (
    <div className="relative w-full max-w-3xl mx-auto" data-testid="prophecy-console">
      <div className="absolute -inset-6 bg-gradient-to-r from-orange-500/25 via-red-500/15 to-orange-500/25 rounded-3xl blur-2xl opacity-80 animate-pulse-slow" />
      <div className="relative rounded-2xl border border-orange-500/30 bg-black/75 backdrop-blur-xl overflow-hidden shadow-[0_0_70px_-15px_rgba(249,115,22,0.5)]">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-white/[0.06] bg-gradient-to-r from-orange-500/[0.07] via-transparent to-orange-500/[0.07]">
          <div className="flex items-center gap-2">
            <Eye className="w-3.5 h-3.5 text-orange-400" />
            <span className="text-[10px] uppercase tracking-[0.25em] text-orange-300/80 font-semibold">
              {phase === 'idle' && 'The Oracle awaits'}
              {isRunning && 'The Oracle is working'}
              {phase === 'done' && 'Prophecy fulfilled'}
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className={`w-1.5 h-1.5 rounded-full ${isRunning ? 'bg-orange-400 animate-pulse' : 'bg-orange-400/40'}`} />
            <span className="text-[10px] text-white/30 tracking-wider">{isRunning ? 'CHANNELING' : 'LISTENING'}</span>
          </div>
        </div>

        {/* Prompt input */}
        <div className="px-5 py-5 min-h-[110px] font-mono relative">
          <div className="flex items-start gap-3">
            <span className="text-orange-400/70 text-xs mt-1.5 select-none">›</span>
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              disabled={isRunning}
              data-testid="prophecy-input"
              rows={3}
              className="flex-1 bg-transparent text-white/90 text-sm md:text-[15px] leading-relaxed outline-none resize-none placeholder:text-white/30 disabled:opacity-60 font-mono"
              placeholder="Speak your prophecy..."
            />
          </div>
          <div className="absolute bottom-2 right-3 flex items-center gap-2">
            <button onClick={cyclePreset} disabled={isRunning} data-testid="prophecy-shuffle"
              className="text-[10px] uppercase tracking-[0.2em] text-white/35 hover:text-orange-300 transition-colors disabled:opacity-30">
              Shuffle example
            </button>
          </div>
        </div>

        {/* Orchestration steps */}
        <div className="px-5 pb-3 pt-1 border-t border-white/[0.04] space-y-1.5">
          {ORCHESTRATION_STEPS.map((step, i) => {
            const status = activeStep > i ? 'done' : activeStep === i ? 'running' : 'pending';
            return (
              <div key={i} data-testid={`orch-step-${i}`}
                className={`flex items-center gap-3 px-2 py-1.5 rounded transition-all ${
                  status === 'running' ? 'bg-orange-500/10' : ''
                }`}>
                <div className={`w-6 h-6 rounded-md flex items-center justify-center border transition-all shrink-0 ${
                  status === 'done' ? 'bg-emerald-500/15 border-emerald-400/40 text-emerald-300' :
                  status === 'running' ? 'bg-orange-500/20 border-orange-400/50 text-orange-300' :
                  'bg-white/[0.02] border-white/[0.06] text-white/30'
                }`}>
                  {status === 'done' ? <CheckCircle2 className="w-3 h-3" /> :
                   status === 'running' ? <Loader2 className="w-3 h-3 animate-spin" /> :
                   <step.icon className="w-3 h-3" />}
                </div>
                <span className={`text-[12px] ${status === 'pending' ? 'text-white/35' : 'text-white/85'}`}>
                  {step.label}
                </span>
                <span className="ml-auto text-[10px] font-mono text-white/30 uppercase tracking-wider">{step.tool}</span>
              </div>
            );
          })}
        </div>

        {/* Output tiles */}
        <div className="grid grid-cols-4 gap-2 px-3 pb-3 pt-1">
          {['plan', 'image', 'video', 'canvas'].map((type, i) => {
            const ready = outputs.some(o => o.type === type);
            const Icon = type === 'plan' ? Brain : type === 'image' ? ImageIcon : type === 'video' ? Video : LayoutGrid;
            return (
              <div key={type} data-testid={`output-${type}`}
                className={`aspect-square rounded-md border flex items-center justify-center relative overflow-hidden transition-all duration-700 ${
                  ready ? 'border-orange-400/40 bg-gradient-to-br from-orange-500/25 via-red-500/15 to-zinc-900 scale-100 opacity-100' :
                  'border-white/[0.06] bg-black/40 scale-95 opacity-50'
                }`}>
                {ready && (
                  <motion.div initial={{ scale: 0, rotate: -45 }} animate={{ scale: 1, rotate: 0 }} transition={{ duration: 0.5, type: 'spring' }}>
                    <Icon className="w-5 h-5 text-orange-300" />
                  </motion.div>
                )}
                {!ready && <Icon className="w-5 h-5 text-white/15" />}
                {ready && (
                  <span className="absolute bottom-1 right-1.5 text-[8px] font-mono text-orange-300/70 uppercase tracking-wider">{type}</span>
                )}
              </div>
            );
          })}
        </div>

        {/* Action bar */}
        <div className="px-4 py-3 border-t border-white/[0.06] bg-black/30 flex items-center justify-between gap-3">
          <div className="text-[10px] uppercase tracking-[0.25em] text-white/35">
            {phase === 'done' ? '↳ Result ready · Tap below to enter the portal' : isRunning ? 'Orchestrating 4 tools in parallel' : 'Try a demo prophecy below'}
          </div>
          {phase === 'done' ? (
            <div className="flex items-center gap-2">
              <button onClick={reset} data-testid="prophecy-reset"
                className="text-[11px] text-white/50 hover:text-white px-3 py-1.5 rounded-md hover:bg-white/[0.04] transition-all">
                Reset
              </button>
              <button onClick={onConsult} data-testid="prophecy-fulfil"
                className="text-[11px] font-semibold px-3.5 py-1.5 rounded-md bg-gradient-to-r from-orange-500 to-red-600 text-white inline-flex items-center gap-1 hover:scale-[1.03] transition-transform">
                Enter Portal <ArrowRight className="w-3 h-3" />
              </button>
            </div>
          ) : (
            <button onClick={runProphecy} disabled={isRunning || !draft.trim()} data-testid="prophecy-run"
              className="text-[11px] font-semibold px-3.5 py-1.5 rounded-md bg-gradient-to-r from-orange-500 to-red-600 text-white inline-flex items-center gap-1.5 hover:scale-[1.03] transition-transform disabled:opacity-50 disabled:scale-100">
              {isRunning ? <><Loader2 className="w-3 h-3 animate-spin" />Channeling</> : <><PlayCircle className="w-3.5 h-3.5" />Run prophecy</>}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Interactive Cinema Lens Picker ────────────────────────────
const CinemaInteractive = () => {
  const [activeIdx, setActiveIdx] = useState(0);
  const preset = CINEMA_PRESETS[activeIdx];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
      {/* Big tile */}
      <div className="relative lg:col-span-8 rounded-2xl overflow-hidden border border-white/[0.06] bg-[#0a0a0f] min-h-[480px] group">
        <AnimatePresence mode="wait">
          <motion.img
            key={preset.id}
            src={IMG_CINEMA_LENS}
            alt="Cinema lens"
            initial={{ opacity: 0, scale: 1.05 }}
            animate={{ opacity: 0.75, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6 }}
            style={{ filter: preset.filter }}
            className="absolute inset-0 w-full h-full object-cover"
          />
        </AnimatePresence>
        <div className="absolute inset-0 transition-colors duration-700" style={{ backgroundColor: preset.tint, mixBlendMode: 'overlay' }} />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/40 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#050505]/80 via-transparent to-transparent" />

        {/* Floating UI control cards */}
        <div className="absolute top-6 left-6 right-6 grid grid-cols-2 md:grid-cols-3 gap-2.5 z-10">
          <FloatingCard icon={Camera} label="Body" value={preset.label.split('·')[0].trim()} />
          <FloatingCard icon={Aperture} label="Lens" value={preset.label.split('·')[1]?.trim() || ''} />
          <FloatingCard icon={Move} label="Motion" value={preset.id === 'sony' ? 'Vertigo · Zoom out' : preset.id === 'red' ? 'Slow push in' : preset.id === 'bm' ? 'Steadicam float' : 'Tracking dolly'} />
        </div>

        {/* Bottom caption */}
        <div className="absolute bottom-6 left-6 right-6 z-10">
          <div className="text-[10px] uppercase tracking-[0.3em] text-orange-300/70 mb-2">// Render Brief</div>
          <AnimatePresence mode="wait">
            <motion.p
              key={preset.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4 }}
              className="text-2xl md:text-3xl font-light leading-tight max-w-md">
              {preset.shot}
            </motion.p>
          </AnimatePresence>
        </div>
      </div>

      {/* Right column — clickable lens chips */}
      <div className="lg:col-span-4 flex flex-col gap-3">
        <div className="text-[10px] uppercase tracking-[0.3em] text-orange-300/70 mb-1 px-1">⌃ Tap a preset</div>
        {CINEMA_PRESETS.map((p, i) => (
          <button key={p.id} onClick={() => setActiveIdx(i)} data-testid={`cinema-preset-${p.id}`}
            className={`text-left rounded-xl p-4 border transition-all duration-300 ${
              i === activeIdx
                ? 'border-orange-400/50 bg-gradient-to-br from-orange-500/15 to-red-500/5 shadow-[0_0_30px_-10px_rgba(249,115,22,0.5)]'
                : 'border-white/[0.06] bg-[#0a0a0f] hover:border-white/15 hover:bg-white/[0.02]'
            }`}>
            <div className="flex items-center justify-between mb-1.5">
              <span className={`text-[10px] font-mono uppercase tracking-wider ${i === activeIdx ? 'text-orange-300' : 'text-white/35'}`}>
                Preset {String(i + 1).padStart(2, '0')}
              </span>
              {i === activeIdx && <span className="text-[9px] text-orange-300 tracking-[0.25em] uppercase">● Live</span>}
            </div>
            <p className={`text-[13px] leading-tight ${i === activeIdx ? 'text-white/95' : 'text-white/65'}`}>{p.label}</p>
          </button>
        ))}
        <div className="mt-2 text-[10px] text-white/30 px-1 leading-relaxed">
          Six bodies. Eight lenses. Sixteen camera motions. Mix and match — the Oracle renders.
        </div>
      </div>
    </div>
  );
};

// ── Live Prophecies Reel ──────────────────────────────────────
const LivePropheciesReel = () => (
  <div className="overflow-hidden -mx-6 md:-mx-12">
    <Marquee gradient gradientColor="#050505" gradientWidth={80} speed={30} pauseOnHover>
      {LIVE_PROPHECIES.concat(LIVE_PROPHECIES).map((p, i) => (
        <div key={i} className="mx-3 w-[280px] shrink-0" data-testid={`prophecy-card-${i % LIVE_PROPHECIES.length}`}>
          <div className="rounded-xl border border-white/[0.06] bg-[#0a0a0f] overflow-hidden hover:border-orange-500/25 transition-colors group">
            <div className="relative aspect-[4/3] overflow-hidden">
              <img src={p.img} alt={p.what} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-black/20" />
              <span className="absolute top-2 left-2 text-[9px] uppercase tracking-[0.2em] px-1.5 py-0.5 rounded bg-black/60 backdrop-blur text-white/85 border border-white/10">{p.kind}</span>
              <button className="absolute top-2 right-2 text-white/70 hover:text-pink-400 transition-colors bg-black/40 backdrop-blur rounded p-1" aria-label="like">
                <Heart className="w-3 h-3" />
              </button>
            </div>
            <div className="p-3">
              <p className="text-[13px] font-medium text-white/90 truncate">{p.what}</p>
              <div className="mt-1 flex items-center justify-between text-[10px] text-white/40">
                <span>{p.user}</span>
                <span className="flex items-center gap-1"><Clock className="w-2.5 h-2.5" />{p.mins} min ago</span>
              </div>
            </div>
          </div>
        </div>
      ))}
    </Marquee>
  </div>
);

// ── Oracle in Action — Auto-looping product demo ──────────────
const ORACLE_DEMO_LOOPS = [
  {
    instruction: "Build me a hero shot for a cyberpunk sneaker drop with a 4-second teaser.",
    steps: [
      { label: "Composing the plan", tool: "Claude 4.5", out: null, type: 'plan' },
      { label: "Generating hero image", tool: "Nano Banana", out: PROPHECY_IMGS.sneaker, type: 'image' },
      { label: "Rendering 4s teaser", tool: "Sora 2", out: PROPHECY_IMGS.tokyo, type: 'video' },
      { label: "Arranging on Canvas", tool: "Delphi Canvas", out: PROPHECY_IMGS.cyber, type: 'canvas' },
    ],
  },
  {
    instruction: "Generate a luxury watch product hero + 3 Instagram captions for the launch.",
    steps: [
      { label: "Drafting brand voice", tool: "Claude 4.5", out: null, type: 'plan' },
      { label: "Product hero shot", tool: "Flux Kontext", out: PROPHECY_IMGS.watch, type: 'image' },
      { label: "Writing captions", tool: "Claude 4.5", out: null, type: 'text' },
      { label: "Carousel layout", tool: "Delphi Canvas", out: PROPHECY_IMGS.carousel, type: 'canvas' },
    ],
  },
  {
    instruction: "Create a fashion editorial of Lyra in golden hour with cloned voiceover.",
    steps: [
      { label: "Casting character", tool: "Delphi Characters", out: IMG_CHAR_FEMALE, type: 'character' },
      { label: "Editorial shot", tool: "Flux Kontext", out: IMG_CHAR_FEMALE, type: 'image' },
      { label: "Cloning voice", tool: "ElevenLabs", out: null, type: 'voice' },
      { label: "Animating performance", tool: "Sora 2", out: PROPHECY_IMGS.cosmetic, type: 'video' },
    ],
  },
];

const OracleInAction = () => {
  const [loopIdx, setLoopIdx] = useState(0);
  const [stepIdx, setStepIdx] = useState(-1);
  const [outputs, setOutputs] = useState([]); // [{type, out}]
  const [typed, setTyped] = useState('');

  // Type the instruction
  useEffect(() => {
    const instr = ORACLE_DEMO_LOOPS[loopIdx].instruction;
    setTyped('');
    setOutputs([]);
    setStepIdx(-1);
    let i = 0;
    const typingInterval = setInterval(() => {
      i++;
      setTyped(instr.slice(0, i));
      if (i >= instr.length) {
        clearInterval(typingInterval);
        // Begin steps
        setTimeout(() => runSteps(), 600);
      }
    }, 28);
    return () => clearInterval(typingInterval);
    // eslint-disable-next-line
  }, [loopIdx]);

  const runSteps = () => {
    const steps = ORACLE_DEMO_LOOPS[loopIdx].steps;
    let cumulative = 0;
    steps.forEach((step, i) => {
      cumulative += 1400;
      setTimeout(() => {
        setStepIdx(i);
        if (step.out) {
          setOutputs(prev => [...prev, { type: step.type, out: step.out, label: step.label }]);
        } else {
          setOutputs(prev => [...prev, { type: step.type, out: null, label: step.label }]);
        }
        // After last step, cycle to next loop
        if (i === steps.length - 1) {
          setTimeout(() => setLoopIdx((loopIdx + 1) % ORACLE_DEMO_LOOPS.length), 2800);
        }
      }, cumulative);
    });
  };

  const currentLoop = ORACLE_DEMO_LOOPS[loopIdx];

  return (
    <div className="relative" data-testid="oracle-in-action">
      <div className="absolute -inset-8 bg-gradient-to-br from-orange-500/15 via-red-500/8 to-transparent rounded-3xl blur-3xl" />
      <div className="relative rounded-2xl border border-orange-500/25 bg-black/80 backdrop-blur-xl overflow-hidden shadow-[0_0_80px_-20px_rgba(249,115,22,0.5)]">
        {/* Mock browser chrome */}
        <div className="flex items-center gap-2 px-4 py-2.5 border-b border-white/[0.05] bg-black/60">
          <div className="flex gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-zinc-700" />
            <span className="w-2.5 h-2.5 rounded-full bg-zinc-700" />
            <span className="w-2.5 h-2.5 rounded-full bg-zinc-700" />
          </div>
          <div className="flex-1 mx-3 px-3 py-0.5 rounded bg-black/40 border border-white/[0.06] text-[10px] text-white/40 font-mono tracking-wide">
            delphiai.ca / oracle
          </div>
          <div className="flex items-center gap-1.5 text-[9px] uppercase tracking-[0.25em] text-orange-300/70 font-semibold">
            <span className="w-1.5 h-1.5 rounded-full bg-orange-400 animate-pulse" />
            Recording
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 min-h-[440px]">
          {/* Left: agent panel */}
          <div className="lg:col-span-2 p-5 border-r border-white/[0.05] bg-gradient-to-b from-black/40 to-transparent">
            <div className="flex items-center gap-2 mb-3">
              <Eye className="w-3.5 h-3.5 text-orange-400" />
              <span className="text-[10px] uppercase tracking-[0.25em] text-orange-300/80 font-semibold">The Oracle · Live</span>
            </div>
            <div className="px-3 py-3 rounded-md bg-black/40 border border-white/[0.06] mb-4 min-h-[80px] font-mono text-[12px] text-white/85 leading-relaxed">
              <span className="text-orange-400/70 mr-1.5">›</span>
              {typed}
              {typed.length === currentLoop.instruction.length ? '' : <span className="inline-block w-1.5 h-3.5 bg-orange-400/80 ml-0.5 align-middle animate-blink" />}
            </div>
            <div className="space-y-1.5">
              {currentLoop.steps.map((step, i) => {
                const done = stepIdx > i;
                const active = stepIdx === i;
                return (
                  <div key={i} className={`flex items-center gap-2.5 px-2 py-1.5 rounded transition-all ${active ? 'bg-orange-500/10' : ''}`}>
                    <div className={`w-5 h-5 rounded flex items-center justify-center border shrink-0 transition-all ${
                      done ? 'bg-emerald-500/15 border-emerald-400/50 text-emerald-300' :
                      active ? 'bg-orange-500/20 border-orange-400/60 text-orange-300' :
                      'bg-white/[0.02] border-white/[0.06] text-white/30'
                    }`}>
                      {done ? <CheckCircle2 className="w-2.5 h-2.5" /> :
                       active ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> :
                       <span className="text-[9px]">{i + 1}</span>}
                    </div>
                    <span className={`text-[11px] ${active || done ? 'text-white/85' : 'text-white/30'}`}>{step.label}</span>
                    <span className="ml-auto text-[9px] font-mono text-white/30 uppercase tracking-wider">{step.tool}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right: live output Canvas */}
          <div className="lg:col-span-3 relative p-5 bg-[radial-gradient(#1a1a25_1px,transparent_1px)] [background-size:24px_24px] bg-[#080810]">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <LayoutGrid className="w-3.5 h-3.5 text-orange-400" />
                <span className="text-[10px] uppercase tracking-[0.25em] text-orange-300/80 font-semibold">Canvas · Output</span>
              </div>
              <span className="text-[9px] text-white/30 font-mono tracking-wider">{outputs.length} / {currentLoop.steps.length}</span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {outputs.map((out, i) => (
                <motion.div
                  key={`${loopIdx}-${i}`}
                  initial={{ opacity: 0, scale: 0.85, y: 12 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  className="aspect-square rounded-lg overflow-hidden border border-orange-500/25 bg-black/40 relative"
                >
                  {out.out ? (
                    <img src={out.out} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center text-center p-3 bg-gradient-to-br from-orange-500/20 to-red-500/10">
                      {out.type === 'plan' && <Brain className="w-6 h-6 text-orange-300 mb-2" />}
                      {out.type === 'text' && <Sparkles className="w-6 h-6 text-orange-300 mb-2" />}
                      {out.type === 'voice' && <Mic className="w-6 h-6 text-orange-300 mb-2" />}
                      <p className="text-[10px] text-white/80 leading-tight">{out.label}</p>
                    </div>
                  )}
                  <span className="absolute bottom-1.5 left-1.5 text-[8px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-black/60 backdrop-blur text-orange-300 border border-orange-500/20">
                    {out.type}
                  </span>
                </motion.div>
              ))}
              {/* Pending slots */}
              {Array.from({ length: Math.max(0, currentLoop.steps.length - outputs.length) }).map((_, i) => (
                <div key={`pending-${i}`} className="aspect-square rounded-lg border border-dashed border-white/[0.08] bg-black/20" />
              ))}
            </div>

            {/* Bottom dots */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-1.5">
              {ORACLE_DEMO_LOOPS.map((_, i) => (
                <button key={i} onClick={() => setLoopIdx(i)} data-testid={`oia-loop-${i}`}
                  className={`h-1 rounded-full transition-all ${i === loopIdx ? 'w-6 bg-orange-400' : 'w-1.5 bg-white/15 hover:bg-white/30'}`} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};


// ── Per-act background hue shifter ────────────────────────────
const ACT_TINTS = {
  1: { primary: 'rgba(249,115,22,0.10)', secondary: 'rgba(220,38,38,0.06)' }, // Prophecy: warm orange ember
  2: { primary: 'rgba(56,189,248,0.07)', secondary: 'rgba(249,115,22,0.05)' }, // Lens: cool film noir blue + amber kiss
  3: { primary: 'rgba(251,191,36,0.08)', secondary: 'rgba(217,70,239,0.05)' }, // Oracle: amber gold + violet mystic
  4: { primary: 'rgba(244,114,182,0.07)', secondary: 'rgba(249,115,22,0.06)' }, // Souls: rose + warm orange
  5: { primary: 'rgba(16,185,129,0.06)', secondary: 'rgba(251,191,36,0.07)' }, // Fulfilled: emerald + gold
};
const ActBackdrop = ({ active }) => {
  const tint = ACT_TINTS[active] || ACT_TINTS[1];
  return (
    <div className="fixed inset-0 z-[5] pointer-events-none transition-all duration-[2000ms]" aria-hidden
      style={{
        background: `
          radial-gradient(ellipse 80% 60% at 20% 30%, ${tint.primary}, transparent 60%),
          radial-gradient(ellipse 70% 50% at 80% 70%, ${tint.secondary}, transparent 65%)
        `,
        mixBlendMode: 'screen',
      }}
    />
  );
};

// ── Chapter Rail (sticky scroll indicator) ────────────────────
const ChapterRail = ({ active }) => {
  const acts = [
    { id: 1, label: 'The Prophecy' },
    { id: 2, label: 'The Lens' },
    { id: 3, label: 'The Oracle' },
    { id: 4, label: 'The Souls' },
    { id: 5, label: 'Fulfilled' },
  ];
  return (
    <div className="fixed right-4 md:right-6 top-1/2 -translate-y-1/2 z-30 hidden lg:flex flex-col items-end gap-3" data-testid="chapter-rail">
      {acts.map((a) => (
        <a key={a.id} href={`#act-${a.id}`} className="group flex items-center gap-2.5 cursor-pointer">
          <span className={`text-[10px] tracking-[0.3em] uppercase font-semibold transition-all ${
            active === a.id ? 'text-orange-300 opacity-100' : 'text-white/30 opacity-0 group-hover:opacity-100'
          }`}>{a.label}</span>
          <span className={`block h-px transition-all duration-500 ${
            active === a.id ? 'w-10 bg-gradient-to-r from-orange-400 to-red-500' : 'w-4 bg-white/20 group-hover:w-6 group-hover:bg-white/50'
          }`} />
        </a>
      ))}
    </div>
  );
};

// ── Massive Roman numeral watermark ───────────────────────────
const ActWatermark = ({ numeral }) => (
  <div className="absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden" aria-hidden>
    <span
      className="font-serif italic text-[28vw] md:text-[22vw] leading-none text-orange-500/[0.025] select-none"
      style={{ fontFamily: 'Georgia, serif' }}
    >
      {numeral}
    </span>
  </div>
);

// ── Chapter Transition (laurel + next-act tease) ──────────────
const ChapterBreak = ({ from, to, idx }) => (
  <div className="relative py-12 px-6 md:px-12" data-testid={`chapter-break-${idx}`}>
    <div className="max-w-3xl mx-auto text-center">
      <LaurelDivider className="mb-5" />
      <p className="text-[11px] tracking-[0.4em] uppercase text-white/30 font-semibold">
        {from} <span className="mx-2 text-orange-400/50">⟶</span> {to}
      </p>
    </div>
  </div>
);

// ── Scroll-aware Oracle Eye (subtle parallax + rotation) ──────
const ScrollEye = ({ children }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  const rotate = useTransform(scrollYProgress, [0, 1], [-3, 3]);
  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [0.96, 1.04, 0.96]);
  return (
    <motion.div ref={ref} style={{ rotate, scale }} className="relative w-full h-full">
      {children}
    </motion.div>
  );
};

const FloatingCard = ({ icon: Icon, label, value }) => (
  <div className="rounded-lg backdrop-blur-xl bg-black/55 border border-white/10 px-3 py-2 flex items-center gap-2.5 hover:border-orange-400/40 transition-all">
    <div className="w-7 h-7 rounded-md bg-gradient-to-br from-orange-500/30 to-red-500/15 border border-orange-400/30 flex items-center justify-center shrink-0">
      <Icon className="w-3.5 h-3.5 text-orange-300" />
    </div>
    <div className="min-w-0">
      <p className="text-[9px] uppercase tracking-[0.2em] text-white/40">{label}</p>
      <p className="text-[12px] text-white/90 truncate font-medium">{value}</p>
    </div>
  </div>
);

// ── Main Page ─────────────────────────────────────────────────
export default function LandingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const heroParallax = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const heroFade = useTransform(scrollYProgress, [0, 0.9], [1, 0]);

  // Cursor-tracking spotlight in hero
  const [mouse, setMouse] = useState({ x: 50, y: 30 });
  // Active chapter tracking for the rail
  const [activeChapter, setActiveChapter] = useState(1);
  useEffect(() => {
    const sections = [1, 2, 3, 4, 5].map(n => document.getElementById(`act-${n}`)).filter(Boolean);
    if (!sections.length) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter(e => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible) {
          const id = parseInt(visible.target.id.replace('act-', ''));
          setActiveChapter(id);
        }
      },
      { rootMargin: '-30% 0px -50% 0px', threshold: [0.1, 0.5, 0.9] }
    );
    sections.forEach(s => observer.observe(s));
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const handler = (e) => {
      const rect = heroRef.current?.getBoundingClientRect();
      if (!rect) return;
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;
      if (x >= 0 && x <= 100 && y >= 0 && y <= 100) setMouse({ x, y });
    };
    window.addEventListener('mousemove', handler);
    return () => window.removeEventListener('mousemove', handler);
  }, []);

  const goApp = () => navigate(user ? '/dashboard' : '/auth');

  const spotlightStyle = useMemo(() => ({
    background: `radial-gradient(circle 380px at ${mouse.x}% ${mouse.y}%, rgba(249,115,22,0.18), transparent 60%)`,
  }), [mouse]);

  return (
    <div className="min-h-screen bg-[#050505] text-white overflow-x-hidden" data-testid="landing-page" style={{ fontFamily: 'Outfit, sans-serif' }}>
      <ActBackdrop active={activeChapter} />
      <ChapterRail active={activeChapter} />
      <style>{`
        @keyframes blink { 0%,49%{opacity:1} 50%,100%{opacity:0} }
        .animate-blink { animation: blink 1s steps(1) infinite; }
        @keyframes pulse-slow { 0%,100%{opacity:.5} 50%{opacity:.9} }
        .animate-pulse-slow { animation: pulse-slow 5s ease-in-out infinite; }
        @keyframes float-ember {
          0%,100% { transform: translateY(0) translateX(0); opacity: .3; }
          50% { transform: translateY(-30px) translateX(15px); opacity: .8; }
        }
        .ember { animation: float-ember 8s ease-in-out infinite; }
        @keyframes ken-burns {
          0%   { transform: scale(1.08) translate(-1.5%, -1%); }
          50%  { transform: scale(1.15) translate(1.5%, 0.5%); }
          100% { transform: scale(1.10) translate(-0.5%, -1.5%); }
        }
        .ken-burns { animation: ken-burns 22s ease-in-out infinite alternate; transform-origin: center; }
        @keyframes shimmer-edge {
          0%, 100% { box-shadow: inset 0 0 60px -20px rgba(249,115,22,0.0); }
          50% { box-shadow: inset 0 0 80px -20px rgba(249,115,22,0.25); }
        }
        .shimmer-edge { animation: shimmer-edge 6s ease-in-out infinite; }
        .grain::after {
          content: ''; position: absolute; inset: 0; pointer-events: none;
          background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4'/%3E%3CfeColorMatrix values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.5 0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
          opacity: 0.18; mix-blend-mode: overlay;
        }
        @keyframes draw-line {
          to { stroke-dashoffset: 0; }
        }
        .constellation-line {
          stroke-dasharray: 200; stroke-dashoffset: 200;
          animation: draw-line 2.5s ease-out forwards;
        }
      `}</style>

      {/* ─────── Header ─────── */}
      <header className="fixed top-0 inset-x-0 z-50 bg-black/40 backdrop-blur-2xl border-b border-white/[0.06]" data-testid="main-nav">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 group" data-testid="logo">
            <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center shadow-lg shadow-orange-500/30 group-hover:shadow-orange-500/50 transition-shadow">
              <Zap className="w-4 h-4 text-white" />
            </div>
            <span className="text-lg font-bold tracking-tight">Delphi</span>
            <span className="text-[9px] tracking-[0.3em] text-orange-400/70 font-semibold uppercase border-l border-white/10 pl-2.5 ml-1 hidden sm:inline">AI · The Oracle</span>
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-[13px] text-white/55">
            <a href="#oracle" className="hover:text-white transition-colors" data-testid="nav-oracle">The Oracle</a>
            <a href="#cinema" className="hover:text-white transition-colors" data-testid="nav-cinema">Cinema</a>
            <a href="#prophecies" className="hover:text-white transition-colors" data-testid="nav-prophecies">Live</a>
            <a href="#pantheon" className="hover:text-white transition-colors" data-testid="nav-pantheon">Pantheon</a>
            <a href="#apps" className="hover:text-white transition-colors" data-testid="nav-apps">Apps</a>
          </nav>
          <div className="flex items-center gap-2">
            {user ? (
              <button onClick={() => navigate('/dashboard')} data-testid="header-dashboard-btn"
                className="px-5 py-2 rounded-full bg-gradient-to-r from-orange-500 to-red-600 text-sm font-semibold hover:opacity-90 transition-opacity">
                Open App
              </button>
            ) : (
              <>
                <button onClick={() => navigate('/auth')} data-testid="header-login-btn"
                  className="hidden sm:inline-flex text-sm text-white/60 hover:text-white px-3 py-2 transition-colors">
                  Log In
                </button>
                <button onClick={() => navigate('/auth')} data-testid="header-signup-btn"
                  className="px-4 md:px-5 py-2 rounded-full bg-gradient-to-r from-orange-500 to-red-600 text-sm font-semibold hover:opacity-90 transition-opacity shadow-lg shadow-orange-500/20">
                Enter Trial Portal
                </button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* ─────── I. HERO ─────── */}
      <section ref={heroRef} id="act-1" className="relative min-h-[100vh] pt-32 pb-24 px-6 md:px-12 overflow-hidden" data-testid="hero-section">
        <ActWatermark numeral="I" />
        {/* Background ember image with parallax */}
        <motion.div style={{ y: heroParallax, opacity: heroFade }} className="absolute inset-0 z-0">
          <img src={IMG_HERO_BG} alt="" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/40 via-[#050505]/60 to-[#050505]" />
        </motion.div>

        {/* Cursor-tracking spotlight */}
        <div className="absolute inset-0 z-[1] pointer-events-none transition-all duration-300" style={spotlightStyle} />

        {/* Floating embers */}
        <div className="absolute inset-0 z-0 pointer-events-none" aria-hidden>
          {[...Array(10)].map((_, i) => (
            <span key={i} className="ember absolute rounded-full bg-orange-400/70 blur-sm"
              style={{
                width: `${3 + i % 4}px`, height: `${3 + i % 4}px`,
                left: `${5 + i * 10}%`, top: `${15 + (i * 9) % 65}%`,
                animationDelay: `${i * 0.6}s`,
              }} />
          ))}
        </div>

        <div className="relative z-10 max-w-7xl mx-auto">
          <motion.div initial="hidden" animate="visible" variants={stagger} className="text-center max-w-5xl mx-auto">
            <motion.div variants={fadeUp}>
              <SectionLabel numeral="I." label="The Prophecy" className="justify-center" />
            </motion.div>

            <motion.h1 variants={fadeUp}
              className="mt-6 text-5xl md:text-6xl lg:text-7xl xl:text-[88px] tracking-tighter font-light leading-[1.05]"
              data-testid="hero-headline">
              Speak. The Oracle <span className="italic font-serif text-orange-300" style={{ fontFamily: 'Georgia, serif' }}>listens</span>.
              <br className="hidden md:block" />
              The image, the motion, the <span className="bg-gradient-to-r from-orange-400 via-orange-300 to-red-400 bg-clip-text text-transparent italic font-serif" style={{ fontFamily: 'Georgia, serif' }}>prophecy</span> — all at once.
            </motion.h1>

            <motion.p variants={fadeUp} className="mt-7 text-base md:text-xl text-white/55 max-w-2xl mx-auto leading-relaxed" data-testid="hero-subhead">
              Delphi AI is the only generative studio with an autonomous creative agent. One prompt — your storyboard, your characters, your campaign — orchestrated end-to-end.
            </motion.p>

            <motion.div variants={fadeUp} className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
              <button onClick={goApp} data-testid="hero-cta-primary"
                className="group inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-gradient-to-r from-orange-500 to-red-600 text-white font-semibold shadow-lg shadow-orange-500/30 hover:shadow-orange-500/50 hover:scale-[1.02] transition-all">
                <Eye className="w-4 h-4" /> Enter the Trial Portal
                <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </button>
              <a href="#prophecy-demo" data-testid="hero-cta-secondary"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full border border-white/15 text-white/80 hover:bg-white/[0.04] hover:border-white/25 transition-all text-sm font-medium">
                <PlayCircle className="w-4 h-4" />Try a live prophecy
              </a>
            </motion.div>

            {/* Interactive Prophecy Console */}
            <motion.div variants={fadeUp} className="mt-16 scroll-mt-24" id="prophecy-demo">
              <InteractiveProphecy onConsult={goApp} />
            </motion.div>

            <motion.p variants={fadeUp} className="mt-8 text-[11px] uppercase tracking-[0.3em] text-white/30">
              ⟡ Free to start · No credit card · 50 monthly credits ⟡
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* ─────── PANTHEON MARQUEE ─────── */}
      <section id="pantheon" className="relative py-16 border-y border-white/[0.05] bg-[#080808]" data-testid="pantheon-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 mb-8">
          <SectionLabel numeral="✦" label="The Pantheon" className="justify-center" />
          <p className="mt-3 text-center text-white/40 text-sm">Eight gods of generation, working under one Oracle.</p>
        </div>
        <Marquee gradient gradientColor="#080808" gradientWidth={120} speed={45} pauseOnHover>
          {PANTHEON.map((m, i) => (
            <div key={i} className="flex items-center gap-3 mx-8" data-testid={`pantheon-${m.name.toLowerCase().replace(/\s/g,'-')}`}>
              <span className="font-mono font-bold tracking-[0.2em] text-white/65 text-lg">{m.name}</span>
              <span className="text-[9px] tracking-[0.25em] uppercase text-orange-400/50 border border-orange-400/20 rounded px-1.5 py-0.5">{m.meta}</span>
              <span className="text-orange-500/40 text-xl ml-4">⟡</span>
            </div>
          ))}
        </Marquee>
      </section>

      <ChapterBreak from="The Prophecy" to="The Lens" idx={1} />
      <span id="cinema" />

      {/* ─────── II. CINEMA STUDIO ─────── */}
      <section id="act-2" className="relative py-24 md:py-32 px-6 md:px-12" data-testid="cinema-studio-section">
        <ActWatermark numeral="II" />
        <div className="max-w-7xl mx-auto">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-100px' }} variants={stagger}>
            <motion.div variants={fadeUp}><SectionLabel numeral="II." label="The Lens · Cinema Studio" /></motion.div>
            <motion.h2 variants={fadeUp} className="mt-4 text-4xl md:text-5xl lg:text-6xl tracking-tighter font-light max-w-3xl leading-[1.1]">
              Pro camera controls.
              <br />
              <span className="italic font-serif text-orange-300" style={{ fontFamily: 'Georgia, serif' }}>AI cinematography</span> that obeys you.
            </motion.h2>
            <motion.p variants={fadeUp} className="mt-5 text-white/55 max-w-xl text-base md:text-lg">
              Choose the body, set the lens, dial the aperture, choose the move. Tap a preset below to preview the look.
            </motion.p>
            <motion.div variants={fadeUp} className="mt-12">
              <CinemaInteractive />
            </motion.div>
          </motion.div>
        </div>
      </section>

      <ChapterBreak from="The Lens" to="The Oracle" idx={2} />
      <span id="oracle" />

      {/* ─────── II.5 ORACLE IN ACTION (auto-loop) ─────── */}
      <section id="act-3" className="relative py-24 md:py-28 px-6 md:px-12 bg-gradient-to-b from-[#050505] via-[#06060a] to-[#050505] border-t border-white/[0.04]" data-testid="oracle-in-action-section">
        <ActWatermark numeral="III" />
        <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse at 30% 50%, rgba(249,115,22,0.06), transparent 50%)' }} />
        <div className="relative max-w-7xl mx-auto">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-100px' }} variants={stagger}>
            <motion.div variants={fadeUp} className="text-center">
              <SectionLabel numeral="▶" label="The Oracle · In Action" className="justify-center" />
            </motion.div>
            <motion.h2 variants={fadeUp} className="mt-4 text-center text-4xl md:text-5xl lg:text-6xl tracking-tighter font-light max-w-4xl mx-auto leading-[1.05]">
              Watch it happen <span className="italic font-serif text-orange-300" style={{ fontFamily: 'Georgia, serif' }}>without you.</span>
            </motion.h2>
            <motion.p variants={fadeUp} className="mt-5 text-center text-white/55 max-w-2xl mx-auto text-base md:text-lg">
              Three live prophecies on a loop. Real models, real outputs, real Canvas. No edits.
            </motion.p>
            <motion.div variants={fadeUp} className="mt-14">
              <OracleInAction />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ─────── III. THE ORACLE ─────── */}
      <section id="oracle" className="relative py-24 md:py-32 px-6 md:px-12 overflow-hidden" data-testid="oracle-section">
        <div className="absolute inset-0 opacity-[0.04] mix-blend-overlay pointer-events-none" aria-hidden style={{ backgroundImage: `url(${IMG_MARBLE})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
        <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse at 80% 50%, rgba(249,115,22,0.10), transparent 50%)' }} />

        <div className="relative max-w-7xl mx-auto">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-100px' }} variants={stagger}
            className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <motion.div variants={fadeUp}><SectionLabel numeral="III." label="The High Priestess · The Oracle" /></motion.div>
              <motion.h2 variants={fadeUp} className="mt-4 text-4xl md:text-5xl lg:text-6xl tracking-tighter font-light leading-[1.05]">
                Don't generate.
                <br />
                <span className="italic font-serif text-orange-300" style={{ fontFamily: 'Georgia, serif' }}>Orchestrate.</span>
              </motion.h2>
              <motion.p variants={fadeUp} className="mt-5 text-white/55 text-base md:text-lg leading-relaxed">
                Every other AI tool generates <em>one thing</em>. The Oracle plans, routes models, executes in parallel, and assembles the result on a Canvas — autonomously.
              </motion.p>

              {/* Live prophecy counter */}
              <motion.div variants={fadeUp} className="mt-8 flex flex-wrap gap-3">
                <StatCard value="14,287" label="Prophecies fulfilled this week" />
                <StatCard value="< 90s" label="Avg time to first output" />
                <StatCard value="8" label="Models orchestrated" />
              </motion.div>

              <motion.div variants={fadeUp} className="mt-8">
                <button onClick={goApp} data-testid="oracle-cta"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-orange-500 to-red-600 text-white font-semibold hover:opacity-90 transition-opacity shadow-lg shadow-orange-500/30">
                  <Eye className="w-4 h-4" /> Enter the Trial Portal <ArrowRight className="w-4 h-4" />
                </button>
              </motion.div>
            </div>

            <motion.div variants={fadeUp} className="relative aspect-square max-w-md lg:max-w-none mx-auto">
              <div className="absolute -inset-10 bg-gradient-to-br from-orange-500/15 via-red-500/10 to-transparent rounded-full blur-3xl animate-pulse-slow" />

              {/* Constellation overlay SVG */}
              <svg className="absolute inset-0 w-full h-full z-[2] pointer-events-none" viewBox="0 0 400 400" aria-hidden>
                <g stroke="rgba(249,115,22,0.5)" strokeWidth="0.4" fill="none">
                  <line x1="60" y1="80" x2="200" y2="200" className="constellation-line" />
                  <line x1="340" y1="100" x2="200" y2="200" className="constellation-line" style={{ animationDelay: '0.4s' }} />
                  <line x1="80" y1="320" x2="200" y2="200" className="constellation-line" style={{ animationDelay: '0.8s' }} />
                  <line x1="340" y1="320" x2="200" y2="200" className="constellation-line" style={{ animationDelay: '1.2s' }} />
                </g>
                <g fill="rgba(249,115,22,0.7)">
                  <circle cx="60" cy="80" r="2" /><circle cx="340" cy="100" r="2" />
                  <circle cx="80" cy="320" r="2" /><circle cx="340" cy="320" r="2" />
                </g>
              </svg>

              <ScrollEye>
                <div className="relative rounded-3xl overflow-hidden border border-orange-500/20 shadow-[0_0_80px_-10px_rgba(249,115,22,0.4)]">
                  <img src={IMG_ORACLE_EYE} alt="The Oracle's Eye" className="w-full h-full object-cover" data-testid="oracle-eye-image" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
                  <div className="absolute bottom-5 left-5 right-5">
                    <Quote className="w-5 h-5 text-orange-400/60 mb-2" />
                    <p className="text-sm md:text-base text-white/85 italic leading-snug" style={{ fontFamily: 'Georgia, serif' }}>
                      "Know thyself — and let the Oracle handle the rest."
                    </p>
                    <p className="mt-1.5 text-[10px] uppercase tracking-[0.3em] text-orange-300/60">— inscribed at Delphi</p>
                  </div>
                </div>
              </ScrollEye>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ─────── BEFORE / AFTER COMPARISON ─────── */}
      <section className="relative py-24 px-6 md:px-12 bg-[#080808] border-y border-white/[0.05]" data-testid="comparison-section">
        <div className="max-w-7xl mx-auto">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-100px' }} variants={stagger}>
            <motion.div variants={fadeUp}><SectionLabel numeral="✣" label="Before / After" className="justify-center" /></motion.div>
            <motion.h2 variants={fadeUp} className="mt-4 text-4xl md:text-5xl lg:text-6xl tracking-tighter font-light text-center max-w-4xl mx-auto leading-[1.05]">
              The handcrafted way. <span className="italic font-serif text-orange-300" style={{ fontFamily: 'Georgia, serif' }}>The Oracle's way.</span>
            </motion.h2>
            <motion.p variants={fadeUp} className="mt-5 text-center text-white/45 max-w-xl mx-auto">
              Real numbers from real workflows. The Oracle isn't faster — it's a different category.
            </motion.p>

            <motion.div variants={fadeUp} className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-5">
              {/* Old way */}
              <div className="rounded-2xl border border-white/[0.06] bg-[#0a0a0f] p-7" data-testid="comparison-old">
                <div className="flex items-center gap-2 mb-5">
                  <span className="text-[10px] uppercase tracking-[0.3em] text-white/40 font-semibold">The Old Way</span>
                  <span className="h-px flex-1 bg-white/[0.06]" />
                </div>
                <h3 className="text-xl md:text-2xl font-light text-white/80 tracking-tight">Hire creatives. Brief them. Iterate. Wait.</h3>
                <ul className="mt-5 space-y-2.5">
                  {COMPARISON.map((c, i) => (
                    <li key={i} className="flex items-center justify-between text-[13px] py-2 border-b border-white/[0.04] last:border-0">
                      <span className="text-white/55">{c.label}</span>
                      <span className="font-mono text-white/85">{c.old}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-5 pt-4 border-t border-white/[0.06]">
                  <span className="text-[10px] uppercase tracking-[0.25em] text-white/40">Total · 4-6 weeks · $8k-$30k</span>
                </div>
              </div>

              {/* Oracle way */}
              <div className="relative rounded-2xl border border-orange-500/30 bg-gradient-to-br from-orange-500/[0.06] via-[#0a0a0f] to-red-500/[0.04] p-7 shadow-[0_0_60px_-20px_rgba(249,115,22,0.4)]" data-testid="comparison-new">
                <div className="absolute -top-3 right-6 px-2.5 py-0.5 rounded-full bg-gradient-to-r from-orange-500 to-red-600 text-[10px] uppercase tracking-[0.2em] font-bold text-white">
                  Recommended
                </div>
                <div className="flex items-center gap-2 mb-5">
                  <Eye className="w-3 h-3 text-orange-300" />
                  <span className="text-[10px] uppercase tracking-[0.3em] text-orange-300 font-semibold">The Oracle's Way</span>
                  <span className="h-px flex-1 bg-gradient-to-r from-orange-500/30 to-transparent" />
                </div>
                <h3 className="text-xl md:text-2xl font-light tracking-tight">Speak once. Watch it materialise.</h3>
                <ul className="mt-5 space-y-2.5">
                  {COMPARISON.map((c, i) => (
                    <li key={i} className="flex items-center justify-between text-[13px] py-2 border-b border-orange-500/10 last:border-0">
                      <span className="text-white/70">{c.label}</span>
                      <span className="font-mono text-orange-300 font-medium">{c.oracle}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-5 pt-4 border-t border-orange-500/15">
                  <span className="text-[10px] uppercase tracking-[0.25em] text-orange-300 font-semibold">Total · 12 minutes · From $0</span>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ─────── LIVE PROPHECIES REEL ─────── */}
      <section id="prophecies" className="relative py-20 px-6 md:px-12 overflow-hidden" data-testid="live-prophecies-section">
        <div className="max-w-7xl mx-auto mb-8">
          <div className="flex items-end justify-between gap-4 flex-wrap">
            <div>
              <SectionLabel numeral="✦" label="Live · Right Now" />
              <h2 className="mt-3 text-3xl md:text-4xl lg:text-5xl tracking-tighter font-light max-w-2xl leading-tight">
                Prophecies fulfilled <span className="italic font-serif text-orange-300" style={{ fontFamily: 'Georgia, serif' }}>moments ago</span>.
              </h2>
            </div>
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-white/40">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              Streaming live
            </div>
          </div>
        </div>
        <LivePropheciesReel />
      </section>

      <ChapterBreak from="The Oracle" to="The Souls" idx={3} />

      {/* ─────── IV. CHARACTERS + CANVAS ─────── */}
      <section id="act-4" className="relative py-24 md:py-32 px-6 md:px-12 bg-[#070708]" data-testid="characters-canvas-section">
        <ActWatermark numeral="IV" />
        <div className="max-w-7xl mx-auto">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-100px' }} variants={stagger}>
            <motion.div variants={fadeUp}><SectionLabel numeral="IV." label="The Souls & The Tapestry" /></motion.div>
            <motion.h2 variants={fadeUp} className="mt-4 text-4xl md:text-5xl lg:text-6xl tracking-tighter font-light max-w-4xl leading-[1.1]">
              Build <span className="italic font-serif text-orange-300" style={{ fontFamily: 'Georgia, serif' }}>characters</span> that speak.
              Compose <span className="italic font-serif text-orange-300" style={{ fontFamily: 'Georgia, serif' }}>worlds</span> on a single canvas.
            </motion.h2>

            <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-5">
              <CharacterCard img={IMG_CHAR_MALE} name="Atlas, the Founder" tags={['Nano Banana 4K', 'Voice cloneable']} testId="character-tile-male" />
              <CharacterCard img={IMG_CHAR_FEMALE} name="Lyra, the Muse" tags={['Editorial HD', 'Animate ready']} testId="character-tile-female" />

              {/* Canvas preview tile */}
              <motion.div variants={fadeUp} className="lg:col-span-4 relative rounded-2xl overflow-hidden border border-white/[0.06] aspect-[3/4] bg-[radial-gradient(#1a1a25_1px,transparent_1px)] [background-size:24px_24px] bg-[#0a0a0f] p-5 flex flex-col" data-testid="canvas-preview-tile">
                <div className="flex items-center gap-2 mb-4">
                  <LayoutGrid className="w-3.5 h-3.5 text-orange-400" />
                  <span className="text-[10px] uppercase tracking-[0.3em] text-orange-300/70">Canvas · Moodboard</span>
                </div>
                <div className="flex-1 grid grid-cols-2 gap-2 content-start">
                  <div className="aspect-square rounded-md bg-gradient-to-br from-orange-500/30 to-red-500/15 border border-white/10" />
                  <div className="aspect-square rounded-md bg-gradient-to-br from-zinc-700/50 to-zinc-900/50 border border-white/10 flex items-center justify-center"><Video className="w-5 h-5 text-orange-300/50" /></div>
                  <div className="aspect-square rounded-md bg-gradient-to-br from-amber-400/20 to-orange-500/10 border border-white/10" />
                  <div className="aspect-square rounded-md p-2 bg-white/[0.03] border border-white/10 flex items-center text-[9px] text-white/50 leading-tight">"Warm mug, cozy vibes. Morning motivation starts here."</div>
                </div>
                <div className="mt-4">
                  <h3 className="text-xl font-light tracking-tight">Canvas</h3>
                  <p className="text-[12px] text-white/45 mt-1">The Oracle drops everything here. Drag, arrange, ship.</p>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      <ChapterBreak from="The Souls" to="Prophecy Fulfilled" idx={4} />
      <span id="apps" />

      {/* ─────── V. APPS ─────── */}
      <section id="act-5" className="relative py-24 md:py-32 px-6 md:px-12" data-testid="apps-grid-section">
        <ActWatermark numeral="V" />
        <div className="max-w-7xl mx-auto">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-100px' }} variants={stagger}>
            <motion.div variants={fadeUp}><SectionLabel numeral="V." label="The Relics · 16 One-Click Apps" /></motion.div>
            <motion.div variants={fadeUp} className="mt-4 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
              <h2 className="text-4xl md:text-5xl lg:text-6xl tracking-tighter font-light max-w-3xl leading-[1.05]">
                Sixteen sacred instruments. One tap each.
              </h2>
              <p className="text-white/45 max-w-sm">Tap a relic. Drop a photo. Get a result that would take a creative team an afternoon.</p>
            </motion.div>

            <motion.div variants={fadeUp} className="mt-12 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 gap-3" data-testid="apps-grid">
              {APPS.map((app, i) => (
                <button key={i} onClick={goApp} data-testid={`app-relic-${i}`}
                  className="group text-left p-5 rounded-xl border border-white/[0.06] bg-gradient-to-br from-white/[0.02] to-transparent hover:border-orange-500/30 hover:from-orange-500/[0.04] transition-all duration-300">
                  <app.icon className="w-6 h-6 text-white/35 group-hover:text-orange-300 transition-colors" />
                  <p className="mt-4 text-[14px] font-medium text-white/85 group-hover:text-white transition-colors">{app.name}</p>
                  <div className="mt-2 flex items-center gap-1 text-[10px] text-white/25 group-hover:text-orange-300/70 transition-colors">
                    Invoke <ArrowRight className="w-2.5 h-2.5" />
                  </div>
                </button>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ─────── VI. USE CASES ─────── */}
      <section className="relative py-24 px-6 md:px-12 border-t border-white/[0.05]" data-testid="use-cases-section">
        <div className="max-w-7xl mx-auto">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-100px' }} variants={stagger}>
            <motion.div variants={fadeUp}><SectionLabel numeral="VI." label="The Devotees" /></motion.div>
            <motion.h2 variants={fadeUp} className="mt-4 text-3xl md:text-4xl tracking-tight font-light max-w-2xl">
              Who comes to consult.
            </motion.h2>
            <motion.div variants={fadeUp} className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {USE_CASES.map((uc, i) => (
                <div key={i} className="p-6 rounded-xl border border-white/[0.06] bg-[#0a0a0f] hover:border-orange-500/20 hover:bg-orange-500/[0.02] transition-all" data-testid={`use-case-${i}`}>
                  <div className="text-[10px] tracking-[0.3em] uppercase text-orange-400/60 font-semibold">For</div>
                  <h3 className="mt-1.5 text-2xl font-light tracking-tight" style={{ fontFamily: 'Georgia, serif' }}>{uc.who}</h3>
                  <p className="mt-4 text-[13px] text-white/50 leading-relaxed">{uc.desc}</p>
                </div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ─────── FINAL CTA ─────── */}
      <section className="relative py-32 px-6 md:px-12 overflow-hidden" data-testid="final-cta">
        <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at 50% 50%, rgba(249,115,22,0.12), transparent 60%)' }} />
        <div className="relative max-w-4xl mx-auto text-center">
          <LaurelDivider className="mb-8" />
          <motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-4xl md:text-6xl lg:text-7xl tracking-tighter font-light leading-[1.05]">
            Prophesy your next
            <br />
            <span className="italic font-serif bg-gradient-to-r from-orange-400 to-red-400 bg-clip-text text-transparent" style={{ fontFamily: 'Georgia, serif' }}>viral moment.</span>
          </motion.h2>
          <p className="mt-6 text-white/50 text-lg max-w-xl mx-auto">
            The Oracle has opened the gates. Step inside.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
            <button onClick={goApp} data-testid="final-cta-primary"
              className="group inline-flex items-center gap-2 px-8 py-4 rounded-full bg-gradient-to-r from-orange-500 to-red-600 text-white font-semibold text-base shadow-lg shadow-orange-500/30 hover:shadow-orange-500/60 hover:scale-[1.02] transition-all">
              <Eye className="w-5 h-5" /> Enter the Trial Portal
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
            <span className="text-[11px] tracking-[0.25em] uppercase text-white/30">⟡ Free · 50 credits ⟡</span>
          </div>
          <LaurelDivider className="mt-16" />
        </div>
      </section>

      {/* ─────── FOOTER ─────── */}
      <footer className="border-t border-white/[0.05] py-12 px-6 md:px-12" data-testid="footer">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row md:items-center justify-between gap-8">
          <div className="flex items-center gap-3">
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center">
              <Zap className="w-3.5 h-3.5 text-white" />
            </div>
            <div>
              <p className="text-[14px] font-semibold tracking-tight">Delphi AI</p>
              <p className="text-[10px] tracking-[0.25em] uppercase text-white/30">The Oracle of Generative Media</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-6 text-[12px] text-white/40">
            <a href="#oracle" className="hover:text-white/80 transition-colors">Oracle</a>
            <a href="#cinema" className="hover:text-white/80 transition-colors">Cinema</a>
            <a href="#prophecies" className="hover:text-white/80 transition-colors">Live</a>
            <a href="#pantheon" className="hover:text-white/80 transition-colors">Pantheon</a>
            <a href="#apps" className="hover:text-white/80 transition-colors">Apps</a>
            <Link to="/auth" className="hover:text-white/80 transition-colors">Sign in</Link>
            <span className="text-white/15">|</span>
            <Link to="/terms" className="hover:text-white/80 transition-colors" data-testid="footer-terms">Terms</Link>
            <Link to="/privacy" className="hover:text-white/80 transition-colors" data-testid="footer-privacy">Privacy</Link>
            <Link to="/refund" className="hover:text-white/80 transition-colors" data-testid="footer-refund">Refunds</Link>
            <Link to="/ai-content-policy" className="hover:text-white/80 transition-colors" data-testid="footer-ai-policy">AI Policy</Link>
          </div>
          <p className="text-[10px] tracking-[0.25em] uppercase text-white/25">© Delphi AI · MMXXVI</p>
        </div>
      </footer>
    </div>
  );
}

// ──────────────── Sub-components ────────────────

const StatCard = ({ value, label }) => (
  <div className="px-4 py-3 rounded-xl border border-orange-500/20 bg-orange-500/[0.04]">
    <div className="text-2xl md:text-3xl font-light tracking-tight bg-gradient-to-br from-orange-300 to-orange-500 bg-clip-text text-transparent" style={{ fontFamily: 'Georgia, serif' }}>{value}</div>
    <div className="text-[10px] uppercase tracking-[0.2em] text-white/45 mt-0.5">{label}</div>
  </div>
);

const CharacterCard = ({ img, video, name, tags, testId, poster }) => (
  <motion.div variants={fadeUp} className="lg:col-span-4 relative rounded-2xl overflow-hidden border border-white/[0.08] aspect-[3/4] group cursor-pointer grain shimmer-edge" data-testid={testId}>
    {video ? (
      <video
        src={video}
        poster={poster || img}
        autoPlay muted loop playsInline
        preload="metadata"
        className="absolute inset-0 w-full h-full object-cover"
        style={{ imageRendering: 'crisp-edges', willChange: 'transform' }}
      />
    ) : (
      <img src={img} alt={name} className="absolute inset-0 w-full h-full object-cover ken-burns" loading="lazy" />
    )}
    {/* Cinematic top vignette */}
    <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/30 to-transparent" />
    <div className="absolute inset-0 bg-gradient-to-br from-orange-500/0 via-transparent to-orange-500/10 mix-blend-overlay opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
    {/* Live badge */}
    <div className="absolute top-3 right-3 z-[2] flex items-center gap-1.5 px-2 py-1 rounded-full bg-black/60 backdrop-blur border border-white/10">
      <span className="w-1.5 h-1.5 rounded-full bg-orange-400 animate-pulse" />
      <span className="text-[9px] uppercase tracking-[0.25em] text-white/85 font-semibold">{video ? 'Sora 2 · Live' : '4K HD · Still'}</span>
    </div>
    {/* Quality chip top-left */}
    <div className="absolute top-3 left-3 z-[2] flex items-center gap-1 px-2 py-1 rounded-full bg-black/60 backdrop-blur border border-orange-500/30">
      <Sparkles className="w-2.5 h-2.5 text-orange-300" />
      <span className="text-[9px] uppercase tracking-[0.2em] text-orange-300 font-semibold">Ultra HD</span>
    </div>
    {/* Bottom content */}
    <div className="absolute bottom-5 left-5 right-5 z-[2]">
      <div className="text-[10px] uppercase tracking-[0.3em] text-orange-300/70 mb-1">Character</div>
      <h3 className="text-2xl font-light tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>{name}</h3>
      <div className="mt-3 flex items-center gap-1.5 flex-wrap">
        {tags.map((t, i) => (
          <span key={i} className="text-[10px] px-2 py-0.5 rounded bg-white/5 border border-white/10 text-white/70 inline-flex items-center gap-1">
            {t.toLowerCase().includes('voice') && <Mic className="w-2.5 h-2.5" />}{t}
          </span>
        ))}
      </div>
    </div>
  </motion.div>
);
